import React, { forwardRef, ForwardRefRenderFunction } from 'react'
import classNames from 'classnames'
import { useToggle } from '@/hooks/useToggle'
import { ShopifyProductWithBundles } from '@/services/Shopify/ShopifyService'
import { useTranslate } from '@/utils/translate/translate-client'
import { BonusGiftCard } from '@/views/PreSales/PreSalesGiveawayView/components/BonusGift/BonusGiftCard'
import { BonusGiftModal } from '@/views/PreSales/PreSalesGiveawayView/components/BonusGift/BonusGiftModal'
import { GiveawayRefundCard } from '@/views/PreSales/PreSalesGiveawayView/components/GiveawayRefundCard'
import { PresaleChipSelectorCard } from '@/views/PreSales/PreSalesGiveawayView/components/PresaleChipSelectorCard'
import { GTitleLG } from '@/views/PreSales/PreSalesGiveawayView/components/Typography'
import { useBonusGift } from '@/views/PreSales/hooks/useBonusGift'

interface Props {
  onRefundInfoClick: () => void
  tickets: ShopifyProductWithBundles[]
  className?: string
  productTitleToFilter: string
  defaultTicketPackId: string
  entriesProductId: string
  projectName: string
  theatricalSlug: string
  mobileProductRef?: (node?: Element | null | undefined) => void
}
const TicketPackSection: ForwardRefRenderFunction<HTMLDivElement, Props> = (
  {
    onRefundInfoClick,
    tickets,
    className,
    mobileProductRef,
    productTitleToFilter,
    defaultTicketPackId,
    entriesProductId,
    projectName,
    theatricalSlug,
  },
  ref,
) => {
  const { t } = useTranslate('tickets')

  const { bonusGift } = useBonusGift(theatricalSlug)
  const [isBonusPrizeModalOpen, setIsBonusPrizeModalOpen] = useToggle(false)

  return (
    <>
      <div ref={ref} className={classNames('w-full px-4 md:px-12', className)}>
        <div
          ref={mobileProductRef}
          className="mx-auto flex w-full max-w-[408px] flex-col space-y-6 py-16 md:top-0 lg:!sticky shortDesktop:!static shortDesktop:top-auto"
        >
          <GTitleLG color="giveaway-text-primary-light">{t('selectATicketPack', 'Select a Ticket Pack')}</GTitleLG>

          <div className="space-y-2">
            {!!bonusGift && (
              <BonusGiftCard
                bonusGift={bonusGift}
                onLearnMoreClick={() => {
                  setIsBonusPrizeModalOpen(true)
                }}
              />
            )}

            <GiveawayRefundCard
              theatricalSlug={theatricalSlug}
              title={t('giveawayRefundGuarantee', 'Showtime Guarantee')}
              description={t(
                'giveawayRefundGuaranteeDescriptionV2',
                '{{projectName}} releases nationwide; if no showtimes appear near you, you can get a full refund. ',
                { projectName },
              )}
              onInfoClick={onRefundInfoClick}
            />
          </div>

          <PresaleChipSelectorCard
            theatricalSlug={theatricalSlug}
            tickets={tickets}
            productTitleToFilter={productTitleToFilter}
            defaultTicketPackId={defaultTicketPackId}
            entriesProductId={entriesProductId}
          />
        </div>
      </div>

      {bonusGift && (
        <BonusGiftModal
          theatricalSlug={theatricalSlug}
          isOpen={isBonusPrizeModalOpen}
          onClose={() => {
            setIsBonusPrizeModalOpen(false)
          }}
          bonusGift={bonusGift}
          onCtaClick={() => {
            setIsBonusPrizeModalOpen(false)
          }}
        />
      )}
    </>
  )
}

export default forwardRef(TicketPackSection)
